import { api } from "@/common/lib/api";
import type { LifecycleState } from "@/features/animals/types";
import { useQuery } from "@tanstack/react-query";

// ─── Types (espejo de src/features/animals/service.ts) ─────────────────────

export type AccountDepreciationItem = {
  animalId: string;
  tag: string;
  lifecycleState: LifecycleState;
  firstCalvingAt: string;
  monthDepreciation: string; // numeric(14,2) → string
  accumulatedDepreciation: string;
  remainingBookValue: string;
  isTerminated: boolean;
};

export type AccountDepreciationResponse = {
  month: string; // YYYY-MM
  items: AccountDepreciationItem[];
  totalMonthDepreciation: string;
  totalAccumulatedDepreciation: string;
  totalRemainingBookValue: string;
  nextCursor: string | null;
};

export type DepreciationStateFilter = "all" | "active" | "terminated";

export type DepreciationListFilters = {
  month?: string;
  state?: DepreciationStateFilter;
  limit?: number;
  cursor?: string;
};

// ─── Query keys ────────────────────────────────────────────────────────────

export function depreciationQueryKey(accountId: string, filters: DepreciationListFilters) {
  return ["depreciation", accountId, filters] as const;
}

// ─── Hook ──────────────────────────────────────────────────────────────────

export function useAccountDepreciation(accountId: string, filters: DepreciationListFilters) {
  return useQuery({
    queryKey: depreciationQueryKey(accountId, filters),
    queryFn: () => {
      const params = new URLSearchParams();
      if (filters.month) params.set("month", filters.month);
      if (filters.state && filters.state !== "all") params.set("state", filters.state);
      if (filters.limit !== undefined) params.set("limit", String(filters.limit));
      if (filters.cursor) params.set("cursor", filters.cursor);
      const qs = params.toString();
      return api.get<AccountDepreciationResponse>(
        `/accounts/${accountId}/depreciation/schedule${qs ? `?${qs}` : ""}`,
      );
    },
    enabled: Boolean(accountId),
  });
}
